import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import './SurveyContent.css';
import QuestionCard from './QuestionCard';
import SubcategoryNav from './SubcategoryNav';
import { API_URL } from '../config';

function SurveyContent({ domain, responses, onResponseChange, comments, onCommentChange }) {
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [showInsights, setShowInsights] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [insights, setInsights] = useState('');
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [insightsError, setInsightsError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (domain.subcategories && domain.subcategories.length > 0) {
      setSelectedSubcategory(domain.subcategories[0]);
    }
  }, [domain]);

  const handleSubcategorySelect = (subcategory) => {
    setSelectedSubcategory(subcategory);
  };

  const handleCopyInsights = async () => {
    try {
      await navigator.clipboard.writeText(insights);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleGetInsights = async (question, domainName, subcategoryName) => {
    setCurrentQuestion(question);
    setShowInsights(true);
    setIsLoadingInsights(true);
    setInsightsError('');

    try {
      const response = await fetch(`${API_URL}/api/question-insights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          questionText: question.text,
          domain: domainName,
          subcategory: subcategoryName
        })
      });

      const data = await response.json();

      if (response.ok) {
        setInsights(data.insights);
      } else {
        setInsightsError(data.error || 'Failed to load insights');
      }
    } catch (error) {
      setInsightsError('Failed to connect to insights service');
    } finally {
      setIsLoadingInsights(false);
    }
  };

  const questionsToDisplay = selectedSubcategory ? selectedSubcategory.questions : [];

  return (
    <div className="survey-content">
      <div className="content-header">
        <h2>{domain.domain}</h2>
        <p className="domain-description">
          Rate how well each statement describes your behavior
        </p>
      </div>
      
      {domain.subcategories && (
        <SubcategoryNav
          subcategories={domain.subcategories}
          selectedSubcategory={selectedSubcategory}
          onSubcategorySelect={handleSubcategorySelect}
          responses={responses}
        />
      )}
      
      <div className="questions-container">
        {questionsToDisplay.map((question) => (
          <QuestionCard
            key={question.id}
            question={question}
            selectedValue={responses[question.id]}
            onResponseChange={onResponseChange}
            comment={comments[question.id] || ''}
            onCommentChange={onCommentChange}
            domain={domain.domain}
            subcategory={selectedSubcategory?.name}
            onGetInsights={handleGetInsights}
          />
        ))}
      </div>

      {showInsights && (
        <>
          <div className="insights-overlay" onClick={() => setShowInsights(false)}></div>
          <div className="insights-modal">
            <div className="insights-header">
              <h3>Additional Insights</h3>
              <div className="insights-header-actions">
                {!isLoadingInsights && !insightsError && (
                  <button className="copy-btn" onClick={handleCopyInsights} title="Copy to clipboard">
                    {copySuccess ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    )}
                  </button>
                )}
                <button className="insights-close" onClick={() => setShowInsights(false)}>×</button>
              </div>
            </div>
            <div className="insights-content">
              {isLoadingInsights ? (
                <div className="insights-loading">
                  <div className="spinner"></div>
                  <p>Generating insights...</p>
                </div>
              ) : insightsError ? (
                <div className="insights-error">
                  <p>{insightsError}</p>
                  <button className="retry-btn" onClick={() => handleGetInsights(currentQuestion, domain.domain, selectedSubcategory?.name)}>Try Again</button>
                </div>
              ) : (
                <div className="insights-text">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{insights}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SurveyContent;
